<script setup lang="ts">
import { Play } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { Task } from '~/types/models';

const props = withDefaults(defineProps<{
    task: Task;
    projectOptions?: Array<{ id: string; name: string; clientId?: string }>;
    clientOptions?: Array<{ id: string; name: string }>;
}>(), {
    projectOptions: () => [],
    clientOptions: () => [],
});

const emit = defineEmits<{ started: [task: Task] }>();

const timeEntriesStore = useTimeEntriesStore();
const projectStore = useProjectStore();
const clientStore = useClientStore();
const { canMutate } = usePermissions();
const starting = ref(false);

const project = computed(() => (
    props.projectOptions.find(item => item.id === props.task.projectId)
    || projectStore.projects.find(item => item.id === props.task.projectId)
    || (projectStore.currentProject?.id === props.task.projectId ? projectStore.currentProject : undefined)
));
const clientId = computed(() => props.task.clientId || project.value?.clientId || '');
const client = computed(() => (
    props.clientOptions.find(item => item.id === clientId.value)
    || clientStore.clients.find(item => item.id === clientId.value)
));
const completed = computed(() => ['done', 'completed', 'archived'].includes(String(props.task.status || '').toLowerCase()));
const activeTimerLabel = computed(() => timeEntriesStore.runningEntry
    ? `A timer is already ${timeEntriesStore.runningEntry.status === 'paused' ? 'paused' : 'running'}.`
    : `Start timer for ${props.task.title}`);

async function startTimer() {
    if (starting.value || timeEntriesStore.runningEntry) return;
    starting.value = true;
    try {
        await timeEntriesStore.startTimer({
            taskId: props.task.id,
            taskTitle: props.task.title,
            projectId: props.task.projectId || project.value?.id,
            projectName: project.value?.name,
            clientId: clientId.value || undefined,
            clientName: client.value?.name,
        });
        emit('started', props.task);
        toast.success(`Timer started for ${props.task.title}.`);
    } catch (error: any) {
        toast.error(error?.message || 'Unable to start the timer.');
    } finally {
        starting.value = false;
    }
}
</script>

<template>
    <TooltipProvider v-if="canMutate && !completed" :delay-duration="300">
        <Tooltip>
            <TooltipTrigger as-child>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    class="hidden size-6 shrink-0 rounded-full text-muted-foreground opacity-0 transition-opacity hover:bg-primary/10 hover:text-primary focus-visible:opacity-100 disabled:opacity-35 sm:inline-flex sm:group-hover:opacity-100"
                    :disabled="starting || timeEntriesStore.saving || Boolean(timeEntriesStore.runningEntry)"
                    :aria-label="activeTimerLabel"
                    @click.stop="startTimer"
                >
                    <Play class="size-3.5 fill-current" />
                </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{{ activeTimerLabel }}</TooltipContent>
        </Tooltip>
    </TooltipProvider>
</template>
